import type {
  AppTypeDescriptor,
  DatabaseMode,
} from '@internal/plugin-waadoo-config-common';

/**
 * The fields of the PostgreSQL section (`DatabaseSection`), by what they
 * set in the CloudNativePG component: its roles (`componentRoles` of the
 * descriptor, genericity plan §2 bis).
 */
export interface DatabaseFields<F extends string = string> {
  /** Role `databaseMode`: one, two or three instances. */
  mode: F;
  /** Role `databaseSync`: the synchronous replication. */
  sync: F;
  cpuRequest: F;
  cpuLimit: F;
  memoryRequest: F;
  memoryLimit: F;
  /** Role `databaseSize`: the volume of each instance. */
  size: F;
}

/**
 * The fields of the descriptor's CloudNativePG component (`kind: cnpg`) by
 * role; undefined without one, or when one of these roles has no field.
 */
export function databaseFieldsOf(
  descriptor: AppTypeDescriptor,
): DatabaseFields | undefined {
  const component = descriptor.databaseComponent;
  const roles = component
    ? descriptor.componentRoles.get(component.id)?.roles
    : undefined;
  if (!roles) return undefined;
  const {
    databaseMode,
    databaseSync,
    cpuRequest,
    cpuLimit,
    memoryRequest,
    memoryLimit,
    databaseSize,
  } = roles;
  return databaseMode &&
    databaseSync &&
    cpuRequest &&
    cpuLimit &&
    memoryRequest &&
    memoryLimit &&
    databaseSize
    ? {
        mode: databaseMode,
        sync: databaseSync,
        cpuRequest,
        cpuLimit,
        memoryRequest,
        memoryLimit,
        size: databaseSize,
      }
    : undefined;
}

/** The words of each mode, in the core's translations. */
export const DATABASE_MODE_KEYS = {
  single: 'configuration.modeSingle',
  ha2: 'configuration.modeHa2',
  ha3zones: 'configuration.modeHa3',
} as const satisfies Record<DatabaseMode, string>;
